import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CreateBidMutation,
  CreateBidMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { BidEntityType } from "lib/types/graphQLEnums";
import { EventData, EventNames, useTracking } from "../../../../tracking";
import { updateLeadQuery } from "../../leads/update_cache/updateLeadEntityCache";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateBidEntityCache } from "./update_cache/updateBidEntityCache";

const CREATE_BID_QUERY = graphql(`
  mutation createBid($bid: CreateBidRequest!, $entities: BidEntitiesInput!) {
    createBid(CreateBidRequest: $bid, entities: $entities) {
      id
      name
      value
      stage {
        name
      }
      lead {
        id
        name
      }
    }
  }
`);

export type CreateBidData = CreateBidMutation["createBid"];
export function useCreateBid(
  trackingData: EventData,
  options?: UseMutationOptions<CreateBidMutation, unknown, CreateBidMutationVariables, unknown>,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(CREATE_BID_QUERY, {
    ...options,
    onSuccess: async (data, variables, context) => {
      await queryClient.refetchQueries({ queryKey: ["bids"] });

      if (variables.entities.entities.length > 0) {
        const entities = variables.entities.entities.map((entity) => ({
          entityId: entity.entityId,
          entityType: entity.entityType,
          bidId: data.createBid.id,
          bidName: data.createBid.name,
        }));

        await updateBidEntityCache({
          queryClient,
          isAdding: true,
          entities,
        });
      }
      const leadId = data.createBid.lead?.id;
      if (leadId) {
        updateLeadQuery(queryClient, leadId, (prevData) => {
          return {
            ...prevData,
            bid: {
              id: data.createBid.id,
              name: data.createBid.name,
            },
          };
        });
      }

      const { id, name, value, stage } = data.createBid;
      const hasCustomBuyer = variables.entities.entities.some(
        (e) => e.entityType === BidEntityType.CustomOrganisation,
      );
      logEvent(EventNames.bidCreated, {
        "Bid ID": id,
        "Bid title": name,
        "Bid value": value,
        "Bid stage": stage.name,
        "Free-text buyer": hasCustomBuyer,
        ...trackingData,
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data: data?.createBid, ...rest };
}
